import {
    ChevronDownIcon,
    ChevronUpIcon,
    CloseIcon,
    Typography,
} from '@eventbrite/marmalade';
import React, { KeyboardEvent, useRef, useState } from 'react';
import { DATE_OPTIONS, DATE_TRANSLATIONS } from './constants';
import styles from './DateFilterDropdown.module.scss';
import { useClickOutside } from './hooks';
import { DateFilterDropdownProps, DateOption } from './interface';

export const DateFilterDropdown = (props: DateFilterDropdownProps) => {
    const { dateSelection, changeDateSelection, enableQuery, pageType } = props;
    let SUFFIX_ICON;
    const DATE_SELECTED = dateSelection.text !== 'default';
    const dateFilterRef = useRef<HTMLDivElement>(null);
    const [isToggled, setIsToggled] = useState(false);
    const TEXT_KEY = dateSelection.text;
    const ICON_PROPS = {
        height: 24,
        width: 24,
        fill: isToggled ? 'white' : 'black',
    };

    useClickOutside({ ref: dateFilterRef, setIsToggled });

    const handleDateSelection = (option: DateOption) => {
        enableQuery();
        changeDateSelection(option);
        setIsToggled(() => !isToggled);
    };

    const handleButtonClick = () => {
        if (!DATE_SELECTED) {
            setIsToggled(() => !isToggled);
        } else {
            enableQuery();
            changeDateSelection(DATE_OPTIONS[0]);
            setIsToggled(false);
        }
    };

    // If the user has selected a date from the dropdown display the close icon
    // Otherwise, display the up or down icon based on the open/closed state of the dropdown
    if (DATE_SELECTED) {
        SUFFIX_ICON = (
            <CloseIcon data-testid="closeIcon" id="closeIcon" {...ICON_PROPS} />
        );
    } else if (isToggled) {
        SUFFIX_ICON = <ChevronUpIcon id="upIcon" {...ICON_PROPS} />;
    } else {
        SUFFIX_ICON = <ChevronDownIcon id="downIcon" {...ICON_PROPS} />;
    }

    const handleKeyboardInput = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.click();
        } else if (e.key === 'Escape') {
            setIsToggled(false);
        }
    };

    return (
        <div
            className={`${styles.toggleChipContainer} toggled--${isToggled} dateSelected--${DATE_SELECTED}`}
            ref={dateFilterRef}
            data-heap-id={`${pageType}-dateFilter-Container`}
            data-testid="dateFilterContainer"
            onKeyUp={handleKeyboardInput}
        >
            <button
                className={`${styles.toggleChip} toggled--${isToggled} dateSelected--${DATE_SELECTED}`}
                onClick={handleButtonClick}
                data-testid="dateFilterButton"
                data-selected={dateSelection.text}
                data-heap-id={`${pageType}-dateFilter-button`}
                aria-expanded={isToggled}
                aria-controls="dateFilterDropdown"
                aria-haspopup="true"
                aria-labelledby="dateFilterButton"
                aria-pressed={isToggled || DATE_SELECTED ? 'true' : 'false'}
            >
                <Typography
                    variant="body-md-bold"
                    color={isToggled || DATE_SELECTED ? 'white' : 'neutral-800'}
                >
                    {DATE_TRANSLATIONS[TEXT_KEY]}
                </Typography>
                <div className={`${styles.iconContainer} iconContainer`}>
                    {SUFFIX_ICON}
                </div>
            </button>
            <div
                className={styles.dropdown}
                data-testid="dropdown"
                data-hidden={!isToggled || DATE_SELECTED}
                data-heap-id={`${pageType}-dateFilter-dropdown`}
                role="list"
                id="dateFilterDropdown"
                aria-labelledby="dateFilterDropdown"
            >
                {DATE_OPTIONS.slice(1).map((option) => (
                    <p
                        key={option.text}
                        onClick={() => handleDateSelection(option)}
                        className={styles.option}
                        data-testid={`dateOption--${option.text}`}
                        data-heap-id={`${pageType}-dateFilter-${option.text}`}
                        role="listitem"
                        tabIndex={0}
                        onKeyUp={handleKeyboardInput}
                    >
                        {DATE_TRANSLATIONS[option.text]}
                    </p>
                ))}
            </div>
        </div>
    );
};
